// My Time Entry — mobile layout (≤767px). One day at a time: pick a day from
// the week strip, then log hours per activity. "Send to BQE" sends every
// unsent change, whatever the day. It shares all state and handlers with the
// web view (TimeEntry.js), so entries and totals match.

// DEPENDENCIES
import React, { useEffect, useRef, useState } from "react";

// MUI COMPONENTS
import {
	Backdrop,
	Button,
	Drawer,
	IconButton,
	SpeedDial,
	SpeedDialAction,
	SpeedDialIcon,
	TextField,
} from "@mui/material";

// MUI ICONS
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import PestControlRodentIcon from "@mui/icons-material/PestControlRodent";
import PetsIcon from "@mui/icons-material/Pets";
import UndoIcon from "@mui/icons-material/Undo";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";

// COMPONENTS
import { MonthCalendarPanel } from "./MonthCalendar";
import FlagCheckboxes from "./TimeEntryFlags";

// DATA / HELPERS
import { useTimeEntryData } from "./timeEntryData";
import {
	hoursInputOk,
	MON3,
	addDays,
	dayIndexOf,
	fmtHours,
	fmtNumber,
	matchesProject,
	onDay,
	toHours,
} from "./timeEntryUtils";

// FUNCTIONAL COMPONENT
export default function MobileTimeEntry({
	loadNotice,
	refreshControl,
	onSecretMode,
	onRevert,
	today,
	week,
	day,
	onPickDate,
	rows,
	projectOrder,
	status,
	unsentCount,
	hoursOnDate,
	selected,
	onSelect,
	onClose,
	onCell,
	onRemove,
	onAddActivity,
	onAddProject,
	onQuickAdd,
	onSend,
	needsMemo,
	cellStateOf,
}) {
	// STATES
	// DATA
	const {
		projects,
		projectOf,
		recentProjectIds,
		activitiesFor,
		activitiesState,
		ensureActivities,
	} = useTimeEntryData();

	const [sheet, setSheet] = useState(null); // "calendar" | "project" | { activityFor: projectId }
	const [query, setQuery] = useState("");
	const [fabOpen, setFabOpen] = useState(false);

	const d = dayIndexOf(day);
	const dayInfo = week[d];
	const dayHours = (list) =>
		list.reduce((a, r) => a + toHours(r.cells[d].hours), 0);
	const dayTotal = dayHours(rows);
	const dayBillable = rows.reduce(
		(a, r) => a + (r.cells[d].billable ? toHours(r.cells[d].hours) : 0),
		0,
	);

	const q = query.trim().toLowerCase();
	const projectMatches = q
		? projects.filter((p) => matchesProject(p, q))
		: recentProjectIds.map(projectOf);

	const activityProjectId = sheet && sheet.activityFor;
	const activityOptions = activityProjectId
		? activitiesFor(
				activityProjectId,
				rows
					.filter(
						(r) => r.projectId === activityProjectId && onDay(r, d),
					)
					.map((r) => r.activityId),
			)
		: [];

	// INPUT HANDLERS
	const closeSheet = () => {
		setSheet(null);
		setQuery("");
	};

	const openActivities = (projectId) => {
		ensureActivities(projectId);
		setSheet({ activityFor: projectId });
	};

	// Picking a project goes straight to choosing its activity
	const pickProject = (projectId) => {
		onAddProject(projectId);
		setQuery("");
		openActivities(projectId);
	};

	const pickActivity = (activityId) => {
		onAddActivity(activityProjectId, activityId, d);
		closeSheet();
	};

	// RENDER
	return (
		<div className="teM">
			{/* Header: title, day navigation, week strip */}
			<div className="teM-head">
				<div className="teM-dayNav">
					<IconButton
						aria-label="Previous day"
						onClick={() => onPickDate(addDays(day, -1))}
					>
						<ChevronLeftIcon />
					</IconButton>
					<div className="teM-dayNavMid">
						<Button
							className="teM-dateBtn"
							color="inherit"
							startIcon={<CalendarMonthIcon color="primary" />}
							onClick={() => setSheet("calendar")}
						>
							{dayInfo.name}, {MON3[day.getMonth()]}{" "}
							{day.getDate()}, {day.getFullYear()}
						</Button>
						{status && status !== "Not sent to BQE" && (
							<span
								className={
									"teStatus" +
									(status === "Not sent to BQE"
										? ""
										: " teStatus--submitted")
								}
							>
								{status}
							</span>
						)}
						{refreshControl}
					</div>
					<IconButton
						aria-label="Next day"
						onClick={() => onPickDate(addDays(day, 1))}
					>
						<ChevronRightIcon />
					</IconButton>
				</div>
				<div className="teM-week">
					{week.map((w, i) => {
						const hrs = hoursOnDate(w.date);
						const on = i === d;
						const cls = [
							"teM-weekDay",
							on && "teM-weekDay--on",
							w.isToday && "teM-weekDay--today",
							w.isWeekend && "teM-weekDay--weekend",
						]
							.filter(Boolean)
							.join(" ");
						const hrsCls = [
							"teM-weekHrs",
							!hrs && "teM-weekHrs--none",
						]
							.filter(Boolean)
							.join(" ");
						return (
							<button
								type="button"
								key={w.iso}
								className={cls}
								onClick={() => onPickDate(w.date)}
							>
								<span className="teM-weekName">{w.name}</span>
								<span className="teM-weekNum">
									{w.date.getDate()}
								</span>
								<span className={hrsCls}>{fmtHours(hrs)}</span>
							</button>
						);
					})}
				</div>
			</div>

			{/* Day's projects */}
			{/* Reminder (with Revert) while this day has changes BQE doesn't have */}
			{unsentCount > 0 && (
				<div className="teM-unsent" role="status">
					<span className="teM-unsentDot" />
					{unsentCount === 1
						? "1 change not sent to BQE"
						: unsentCount + " changes not sent to BQE"}
					<Button
						size="small"
						color="inherit"
						className="teUnsent-revert"
						startIcon={<UndoIcon fontSize="small" />}
						onClick={onRevert}
					>
						Revert
					</Button>
				</div>
			)}
			<div className="teM-body">
				{loadNotice}
				{projectOrder.map((projectId) => {
					const project = projectOf(projectId);
					const projectRows = rows.filter(
						(r) => r.projectId === projectId && onDay(r, d),
					);
					return (
						<div key={projectId} className="teM-card">
							<div className="teM-cardHead">
								<span className="teM-cardName">
									{project.name}
								</span>
								<span className="teM-cardTotal">
									{fmtHours(dayHours(projectRows))}
								</span>
								<IconButton
									aria-label="Remove project"
									className="teDeleteBtn"
									onClick={() =>
										onRemove("project", projectId)
									}
								>
									<DeleteOutlineIcon />
								</IconButton>
								<IconButton
									aria-label="Add an activity"
									color="primary"
									onClick={() => openActivities(projectId)}
								>
									<AddIcon />
								</IconButton>
							</div>
							{projectRows.length === 0 && (
								<button
									type="button"
									className="teM-emptyRow"
									onClick={() => openActivities(projectId)}
								>
									Choose an activity to log time
								</button>
							)}
							{projectRows.map((row) => (
								<MobileActivityRow
									key={row.id}
									row={row}
									day={d}
									dayLabel={dayInfo.long}
									open={
										!!selected &&
										selected.rowId === row.id &&
										selected.day === d
									}
									onToggle={() =>
										selected &&
										selected.rowId === row.id &&
										selected.day === d
											? onClose()
											: onSelect(row.id, d)
									}
									onFocusHours={() => onSelect(row.id, d)}
									onClose={onClose}
									onCell={(patch) => onCell(row.id, d, patch)}
									onRemove={() => onRemove("day", row.id, d)}
									needsMemo={needsMemo}
									state={cellStateOf(row, d)}
								/>
							))}
						</div>
					);
				})}

				{!loadNotice && projectOrder.length === 0 && (
					<div className="teM-empty">
						<span>No time on this week yet.</span>
						<Button
							variant="outlined"
							startIcon={<CreateNewFolderIcon />}
							onClick={() => setSheet("project")}
						>
							Add a project
						</Button>
					</div>
				)}
			</div>

			{/* Day totals and Send to BQE */}
			<div className="teM-bottom">
				<div className="teM-bottomTotals">
					<span className="teM-bottomMain">
						{fmtNumber(dayTotal)} hrs
					</span>
					<span className="teM-bottomSplit">
						Billable {fmtNumber(dayBillable)} · Non-billable{" "}
						{fmtNumber(dayTotal - dayBillable)}
					</span>
				</div>
				<Button
					variant={unsentCount ? "contained" : "outlined"}
					disableElevation
					startIcon={<SendIcon />}
					onClick={onSend}
				>
					Send to BQE
				</Button>
			</div>

			{/* Add menu (hidden while a row is open; it has its own actions) */}
			{!selected && (
				<>
					<Backdrop
						open={fabOpen}
						className="teM-fabBackdrop"
						onClick={() => setFabOpen(false)}
					/>
					<SpeedDial
						ariaLabel="Add time"
						className="teM-fab"
						icon={<SpeedDialIcon />}
						open={fabOpen}
						// Only a tap opens it — not focus returning after a sheet or
						// Quick Add closes, or hover
						onOpen={(e, reason) =>
							reason === "toggle" && setFabOpen(true)
						}
						onClose={() => setFabOpen(false)}
					>
						<SpeedDialAction
							icon={<CreateNewFolderIcon />}
							tooltipTitle="Add a project"
							tooltipOpen
							onClick={() => {
								setFabOpen(false);
								setSheet("project");
							}}
						/>
						<SpeedDialAction
							icon={<BoltIcon />}
							tooltipTitle="Quick Add"
							tooltipOpen
							onClick={() => {
								setFabOpen(false);
								onQuickAdd();
							}}
						/>
						{onSecretMode && (
							<SpeedDialAction
								icon={<PestControlRodentIcon />}
								tooltipTitle="Super Secret IT Time Entry Mode"
								tooltipOpen
								onClick={() => {
									setFabOpen(false);
									onSecretMode("it");
								}}
							/>
						)}
						{onSecretMode && (
							<SpeedDialAction
								icon={<PetsIcon />}
								tooltipTitle="Super Secret CS Time Entry Mode"
								tooltipOpen
								onClick={() => {
									setFabOpen(false);
									onSecretMode("clientServices");
								}}
							/>
						)}
					</SpeedDial>
				</>
			)}

			{/* Calendar sheet */}
			<Drawer
				anchor="bottom"
				open={sheet === "calendar"}
				onClose={closeSheet}
				PaperProps={{ className: "teM-sheet" }}
			>
				<span className="teM-sheetGrip" />
				<MonthCalendarPanel
					mode="day"
					selectedDate={day}
					today={today}
					hasHours={(x) => hoursOnDate(x) > 0}
					onPick={(x) => {
						onPickDate(x);
						closeSheet();
					}}
					footerLabel="Today"
					onFooter={() => {
						onPickDate(today);
						closeSheet();
					}}
				/>
			</Drawer>

			{/* Add a project sheet */}
			<Drawer
				anchor="bottom"
				open={sheet === "project"}
				onClose={closeSheet}
				PaperProps={{ className: "teM-sheet teM-sheet--tall" }}
			>
				<span className="teM-sheetGrip" />
				<div className="teM-sheetHead">
					<span className="teM-sheetTitle">Add a project</span>
					<IconButton aria-label="Close" onClick={closeSheet}>
						<CloseIcon />
					</IconButton>
				</div>
				<div className="teM-sheetSearch">
					<TextField
						fullWidth
						size="small"
						value={query}
						placeholder="Search projects…"
						onChange={(e) => setQuery(e.target.value)}
					/>
				</div>
				<div className="teM-sheetList">
					<div className="teM-sheetListHead">
						{q ? "Matches" : "Recent for you"}
					</div>
					{projectMatches.slice(0, 20).map((p) => (
						<button
							type="button"
							key={p.id}
							className="teM-option"
							onClick={() => pickProject(p.id)}
						>
							<span className="teOption-name">{p.name}</span>
						</button>
					))}
					{q && projectMatches.length === 0 && (
						<div className="teM-sheetNone">No Results Found</div>
					)}
				</div>
			</Drawer>

			{/* Add an activity sheet */}
			<Drawer
				anchor="bottom"
				open={Boolean(activityProjectId)}
				onClose={closeSheet}
				PaperProps={{ className: "teM-sheet teM-sheet--tall" }}
			>
				<span className="teM-sheetGrip" />
				<div className="teM-sheetHead">
					<span className="teM-sheetTitleWrap">
						<span className="teM-sheetTitle">Add an activity</span>
						<span className="teM-sheetSub">
							{activityProjectId &&
								projectOf(activityProjectId).name}
						</span>
					</span>
					<IconButton aria-label="Close" onClick={closeSheet}>
						<CloseIcon />
					</IconButton>
				</div>
				<div className="teM-sheetList">
					{activityOptions.map((a) => (
						<button
							type="button"
							key={a.id}
							className="teM-option"
							onClick={() => pickActivity(a.id)}
						>
							<span
								className={
									"teOption-name" +
									(a.id ===
									projectOf(activityProjectId)
										.mostUsedActivityId
										? " teOption-name--blue"
										: "")
								}
							>
								{a.name}
							</span>
							<span className="teOption-sub">
								{a.description}
							</span>
						</button>
					))}
					{activityProjectId && activityOptions.length === 0 && (
						<div className="teM-sheetNone">
							{activitiesState(activityProjectId) === "ready"
								? "Every activity is already on this day"
								: activitiesState(activityProjectId) === "error"
									? "Couldn't load activities. Close and try again."
									: "Loading activities…"}
						</div>
					)}
				</div>
			</Drawer>
		</div>
	);
}

// One activity line for the selected day: tap to expand memo and flags
function MobileActivityRow({
	row,
	day,
	dayLabel,
	open,
	onToggle,
	onFocusHours,
	onClose,
	onCell,
	onRemove,
	needsMemo,
	state,
}) {
	const { activityOf } = useTimeEntryData();
	const activity = activityOf(row.activityId);
	const cell = row.cells[day];
	const has = toHours(cell.hours) > 0;
	const missing = needsMemo(cell);
	const locked = Boolean(cell.lockReason);

	const memoCls =
		"teM-rowMemo" +
		(cell.memo
			? ""
			: missing
				? " teM-rowMemo--needed"
				: " teM-rowMemo--hint");
	const hoursCls =
		"teM-hours" +
		(missing ? " teM-hours--needed" : has ? " teM-hours--has" : "") +
		(locked ? " teM-hours--locked" : "") +
		(state ? " teCell--" + state : "");

	// Bring the whole expanded row (memo, flags, Done) into view
	const ref = useRef(null);
	useEffect(() => {
		if (open && ref.current) {
			ref.current.scrollIntoView({
				block: "nearest",
				behavior: "smooth",
			});
		}
	}, [open]);

	return (
		<div
			ref={ref}
			className={"teM-activity" + (open ? " teM-activity--open" : "")}
		>
			<div className="teM-row">
				<button
					type="button"
					className="teM-rowMain"
					onClick={onToggle}
				>
					<span className="teM-rowName">{activity.name}</span>
					<span className="teM-rowDesc">{activity.description}</span>
					{/* Hidden while open — the memo box below shows it */}
					{!open && (
						<span className={memoCls}>
							{cell.memo ||
								(has ? "Memo needed" : "Tap to add memo")}
						</span>
					)}
				</button>
				<input
					data-te-cell={row.id + "-" + day}
					className={hoursCls}
					value={cell.hours}
					placeholder="—"
					inputMode="decimal"
					readOnly={locked}
					aria-label={activity.name + " hours · " + dayLabel}
					onFocus={onFocusHours}
					onChange={(e) =>
						!locked &&
						hoursInputOk(e.target.value, cell.hours) &&
						onCell({ hours: e.target.value })
					}
				/>
			</div>

			{open && (
				<div className="teM-detail">
					<TextField
						multiline
						minRows={2}
						disabled={locked}
						size="small"
						label={"Memo · " + dayLabel}
						value={cell.memo}
						placeholder="What was done, in your own words…"
						error={missing}
						helperText={missing ? "Memo is required" : undefined}
						onChange={(e) => onCell({ memo: e.target.value })}
					/>
					<FlagCheckboxes
						className="teM-flags"
						itemClassName="teM-flag"
						values={cell}
						disabled={locked}
						onToggle={(key) => onCell({ [key]: !cell[key] })}
					/>
					<div className="teM-detailActions">
						<Button
							variant="outlined"
							color="error"
							startIcon={<DeleteOutlineIcon />}
							onClick={onRemove}
							disabled={locked}
						>
							Remove
						</Button>
						<span className="teM-spacer" />
						<Button
							variant="contained"
							disableElevation
							onClick={onClose}
						>
							Done
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
